import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BasicFlow } from './BasicFlow';

import { ErrorBoundary } from '../ErrorBoundary';
import { isBeneficiariesStepComplete, isCompanyFinancialStepComplete, isContactsStepComplete } from '../../utils';
import type { ClaimRequest, Contact, Member, Step } from '../../types';


export const BusinessClaim: React.FC<BusinessClaimProps> = ({
  organization,
  members,
  contacts,
  currentUser,
  claim,
  privacyPolicyAccepted,
}) => {
  const { t } = useTranslation();

  /**
   * Derive steps status from data - single source of truth
   * This replaces localStorage logic and works on refresh
   */
  const computeSteps = (): Step[] => {
    const steps: Step[] = [
      { 
        label: t('kyb_flow.steps.step_1'), 
        status: isContactsStepComplete(contacts, members) ? 'completed' : 'pending' 
      },
      { 
        label: t('kyb_flow.steps.step_2'), 
        status: isBeneficiariesStepComplete(contacts) ? 'completed' : 'pending' 
      },
      {
        label: t('kyb_flow.steps.step_3'),
        status: isCompanyFinancialStepComplete(claim) ? 'completed' : 'pending'
      },
    ];

    // Only include privacy policy step if not accepted
    if (!privacyPolicyAccepted) {
      steps.push({ 
        label: t('kyb_flow.steps.step_4'), 
        status: 'pending'
      });
    }

    steps.push({ 
      label: t('kyb_flow.steps.step_5'), 
      status: 'pending'
    });

    return steps;
  };

  const initialSteps = computeSteps();

  // Always start with overview (null) on page load
  // This shows completed steps with checkmarks
  // User clicks "Continue" button to jump to first pending step
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [steps, setSteps] = useState<Step[]>(initialSteps);

  return (
    <ErrorBoundary>
        <BasicFlow
          organization={organization}
          members={members}
          contacts={contacts}
          currentUser={currentUser}
          claim={claim}
          privacyPolicyAccepted={privacyPolicyAccepted}
          steps={steps}
          setSteps={setSteps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
    </ErrorBoundary>
  );
};

interface BusinessClaimProps {
  organization: any;
  // organizationType: string;
  members: Member[];
  contacts: Contact[];
  currentUser?: any;
  currentUserId?: number;
  claim: ClaimRequest;
  locale: string;
  // isOutdated: boolean;
  privacyPolicyAccepted: boolean;
}
